import { ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'error' | 'info';

const TONES: Record<BadgeTone, { bg: string; fg: string; dot: string }> = {
  neutral: { bg: 'var(--color-bg-secondary)', fg: 'var(--color-text-secondary)', dot: '#9CA3AF' },
  brand: {
    bg: 'rgb(var(--color-black-rgb) / 0.08)',
    fg: 'var(--color-black)',
    dot: 'var(--color-black)',
  },
  success: { bg: '#ECFDF5', fg: '#047857', dot: '#10B981' },
  warning: { bg: '#FFFBEB', fg: '#B45309', dot: '#F59E0B' },
  error: { bg: '#FEF2F2', fg: '#B91C1C', dot: '#EF4444' },
  info: { bg: '#EFF6FF', fg: '#1D4ED8', dot: '#3B82F6' },
};

const Root = styled.span<{ $tone: BadgeTone; $size: 'sm' | 'md' }>`
  ${tw`inline-flex items-center gap-1.5 font-medium rounded-full whitespace-nowrap align-middle`}
  ${({ $size }) => ($size === 'sm' ? tw`text-[11px] px-2 h-5` : tw`text-xs px-2.5 h-6`)}
  background-color: ${({ $tone }) => TONES[$tone].bg};
  color: ${({ $tone }) => TONES[$tone].fg};
`;

const Dot = styled.span<{ $tone: BadgeTone }>`
  ${tw`inline-block rounded-full shrink-0`}
  width: 6px;
  height: 6px;
  background-color: ${({ $tone }) => TONES[$tone].dot};
`;

export function Badge({
  children,
  tone = 'neutral',
  size = 'md',
  dot = false,
  className,
  title,
  'data-testid': testId,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
  title?: string;
  'data-testid'?: string;
}) {
  return (
    <Root $tone={tone} $size={size} className={className} title={title} data-testid={testId}>
      {dot && <Dot $tone={tone} aria-hidden />}
      {children}
    </Root>
  );
}

/** Map a free-form status string onto a badge tone. */
export function statusTone(status?: string | null): BadgeTone {
  const s = (status ?? '').toUpperCase();
  if (
    [
      'APPROVED',
      'ACTIVE',
      'BOOKED',
      'COMPLETED',
      'PAID',
      'SUCCEEDED',
      'RESOLVED',
      'VERIFIED',
      'LISTED',
    ].includes(s)
  ) {
    return 'success';
  }
  if (
    [
      'PENDING',
      'SUBMITTED',
      'IN_REVIEW',
      'UNDER_REVIEW',
      'PAUSED',
      'PROCESSING',
      'AWAITING',
    ].includes(s)
  ) {
    return 'warning';
  }
  if (
    [
      'REJECTED',
      'CANCELLED',
      'CANCELED',
      'FAILED',
      'DISPUTED',
      'DEACTIVATED',
      'REVOKED',
      'EXPIRED',
    ].includes(s)
  ) {
    return 'error';
  }
  if (['INFO', 'DRAFT', 'OPEN', 'NEW'].includes(s)) {
    return 'info';
  }
  return 'neutral';
}
